import Link from "next/link";
import React from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";

const MCP_URL = "https://app.frazzlecrm.com/api/mcp";

const TOOL_GROUPS: { title: string; tools: [string, string][] }[] = [
  {
    title: "Automations",
    tools: [
      ["create_automation", "Draft a full flow: trigger, emails, SMS, waits, branches, goals"],
      ["update_automation", "Edit steps on an existing flow"],
      ["set_automation_status", "Flip a flow between draft, live and paused"],
      ["analyze_automations", "Audit every flow for conflicts, dead ends and overlaps"],
      ["list_automations / get_automation", "Read what exists and how it's wired"],
    ],
  },
  {
    title: "Funnel maps",
    tools: [
      ["get_map / list_maps", "Read the canvas — nodes, edges, flows, notes"],
      ["annotate_map", "Leave notes and callouts right on the map"],
    ],
  },
  {
    title: "Contacts & templates",
    tools: [
      ["search_contacts", "Find contacts by name, email, tag or field"],
      ["create_email_template / list_email_templates", "Draft reusable emails with merge fields"],
      ["get_schema_reference", "Self-serve docs: the AI looks up field shapes itself"],
    ],
  },
];

const PROMPTS = [
  "Build a 5-email onboarding sequence for trials that go quiet after day 3. Draft only — I'll review.",
  "Audit my automations for contacts that could get double-emailed.",
  "Search contacts tagged 'hot-lead' who haven't been emailed in 30 days.",
  "Annotate the webinar map with what you'd improve about the show-up sequence.",
];

/** AI & MCP docs/tutorial page (www.frazzlecrm.com/ai) — indexable. */
export default function AiPage() {
  return (
    <SiteShell
      title="Connect your AI to FrazzleCRM — MCP server docs"
      description="FrazzleCRM ships a built-in MCP server: connect Claude, ChatGPT or any MCP client and let your AI build automations, audit funnels, search contacts and annotate maps — with you approving what goes live."
    >

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-6xl px-6 pb-14 pt-16 text-center">
          <div className="mx-auto mb-4 w-fit rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[12px] font-semibold text-indigo-700">
            Listed in the official MCP Registry
          </div>
          <h1 className="mx-auto max-w-3xl text-[38px] font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-[46px]">
            The CRM your{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              AI can drive
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-[16px] leading-relaxed text-slate-600">
            FrazzleCRM has a built-in MCP server. Point Claude, ChatGPT or any
            MCP-capable agent at your workspace and it can build automations,
            audit funnels, search contacts and annotate maps — while you keep
            the go-live switch.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-4">
        <h2 className="text-[24px] font-bold text-slate-900">
          Connect in 3 steps
        </h2>
        <ol className="mt-6 space-y-6">
          <li className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
            <div className="text-[13px] font-bold uppercase tracking-widest text-indigo-500">
              Step 1
            </div>
            <h3 className="mt-1 text-[17px] font-bold text-slate-900">
              Mint an API key
            </h3>
            <p className="mt-2 text-[14px] leading-relaxed text-slate-600">
              In the app, go to <strong>Settings → API Keys</strong> and create
              a key. It starts with <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[12.5px]">frz_</code>{" "}
              and scopes the AI to exactly one workspace. AI access is part of
              the <a href="/pricing" className="font-medium text-indigo-600 hover:text-indigo-500">Pro plan</a>.
            </p>
          </li>
          <li className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
            <div className="text-[13px] font-bold uppercase tracking-widest text-indigo-500">
              Step 2
            </div>
            <h3 className="mt-1 text-[17px] font-bold text-slate-900">
              Add the connector
            </h3>
            <p className="mt-2 text-[14px] leading-relaxed text-slate-600">
              The server speaks Streamable HTTP at:
            </p>
            <pre className="mt-3 overflow-x-auto rounded-xl bg-slate-900 p-4 text-[12.5px] leading-relaxed text-slate-100">{`${MCP_URL}
Authorization: Bearer frz_your_key`}</pre>
            <p className="mt-4 text-[14px] text-slate-600">
              <strong>claude.ai</strong> — Settings → Connectors → Add custom
              connector, paste the URL and your key.
            </p>
            <p className="mt-2 text-[14px] text-slate-600">
              <strong>Claude Code</strong> — one command:
            </p>
            <pre className="mt-2 overflow-x-auto rounded-xl bg-slate-900 p-4 text-[12.5px] leading-relaxed text-slate-100">{`claude mcp add --transport http frazzlecrm \\
  ${MCP_URL} \\
  --header "Authorization: Bearer frz_your_key"`}</pre>
          </li>
          <li className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
            <div className="text-[13px] font-bold uppercase tracking-widest text-indigo-500">
              Step 3
            </div>
            <h3 className="mt-1 text-[17px] font-bold text-slate-900">
              Ask for a funnel
            </h3>
            <ul className="mt-3 space-y-2">
              {PROMPTS.map((p) => (
                <li
                  key={p}
                  className="rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-2.5 text-[13.5px] italic text-slate-600"
                >
                  “{p}”
                </li>
              ))}
            </ul>
          </li>
        </ol>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-12">
        <h2 className="text-[24px] font-bold text-slate-900">
          What your AI can do
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
          {TOOL_GROUPS.map((g) => (
            <div key={g.title} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <h3 className="text-[15px] font-bold text-slate-900">{g.title}</h3>
              <ul className="mt-3 space-y-2.5">
                {g.tools.map(([name, desc]) => (
                  <li key={name} className="text-[13px] leading-relaxed text-slate-600">
                    <code className="rounded bg-indigo-50 px-1.5 py-0.5 text-[11.5px] font-semibold text-indigo-700">
                      {name}
                    </code>
                    <span className="mt-0.5 block">{desc}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-5 text-[13.5px] text-slate-500">
          Curious how the server itself is built? Read the engineering
          write-up:{" "}
          <Link
            href="/blog/stateless-mcp-server-nextjs"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            a stateless Streamable HTTP MCP server inside Next.js
          </Link>
          .
        </p>
      </section>

      <section className="border-y border-slate-100 bg-slate-50/60">
        <div className="mx-auto max-w-4xl px-6 py-12">
          <h2 className="text-[24px] font-bold text-slate-900">
            Agents build. You approve.
          </h2>
          <p className="mt-3 max-w-2xl text-[14.5px] leading-relaxed text-slate-600">
            Everything an AI creates lands as a <strong>draft</strong> on your
            canvas — visible, inspectable, editable. Nothing emails a real
            contact until a human flips it live. That&apos;s the pattern we used to
            wire 17 production automations for a coding school in an
            afternoon: Claude drafted, a person reviewed, then the switch.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16 text-center">
        <h2 className="text-[28px] font-bold text-slate-900">
          Give your AI a CRM it can actually use.
        </h2>
        <a
          href={`${APP_URL}/signup`}
          className="mt-6 inline-block rounded-xl bg-indigo-600 px-7 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
        >
          Start free — upgrade when you connect
        </a>
      </section>
    </SiteShell>
  );
}
